import { useState, useEffect } from "react";
import Select from "react-select";
import axiosInstance from "../api/axiosInstance";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface Route {
  id: number;
  name: string;
}



const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Others", label: "Others" },
];

export default function CreateReportModal({ patientPhone, onClose }) {
  const [comment, setComment] = useState("");
  const [requestForDiagnosis, setRequestForDiagnosis] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [routes, setRoutes] = useState<Route[]>([]);

  // New Fields State
  const [patientName, setPatientName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [occupation, setOccupation] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [chiefComplaints, setChiefComplaints] = useState("");
  const [associatedSymptom, setAssociatedSymptom] = useState({
    region: "",
    inspection: "",
    palpation: "",
    rangeOfMotion: "",
    muscleStrength: "",
    specialTests: "",
  });
  const [drugAllergy, setDrugAllergy] = useState("");
  const [advice, setAdvice] = useState("");
  const [exerciseNotes, setExerciseNotes] = useState("");

  const [medicines, setMedicines] = useState([
    {
      name: "",
      days: 1,
      dosage: "",
      route: "",
      morning: { take: false, beforeFood: false, afterFood: false },
      afternoon: { take: false, beforeFood: false, afterFood: false },
      evening: { take: false, beforeFood: false, afterFood: false },
      night: { take: false, beforeFood: false, afterFood: false },
    },
  ]);


  const addMedicine = () => {
    setMedicines([
      ...medicines,
      {
        name: "",
        days: 1,
        dosage: "",
        route: "",
        morning: { take: false, beforeFood: false, afterFood: false },
        afternoon: { take: false, beforeFood: false, afterFood: false },
        evening: { take: false, beforeFood: false, afterFood: false },
        night: { take: false, beforeFood: false, afterFood: false },
      },
    ]);
  };



  const getRoutes = async () => {
    const res = await axiosInstance.get("/reports/routes");
    console.log("Routes", res.data);
    setRoutes(res.data);
  }

  useEffect(() => {
    getRoutes();
  }, [])

  const save = async () => {
    await axiosInstance.post("/reports/create", {
      patientPhone,
      comment,
      requestForDiagnosis,
      followUpDate,
      medicines,
      patientName,
      age: Number(age),
      gender,
      occupation,
      height,
      weight,
      chiefComplaints,
      associatedSymptom, // This is now an array of objects
      drugAllergy,
      advice,
      exerciseNotes
    });

    onClose();
    window.location.reload();
  };

  const deleteMedicine = (index: number) => {
    const copy = [...medicines];
    copy.splice(index, 1);
    setMedicines(copy);
  };

  // Removed addSymptom and deleteSymptom functions

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold mb-3">Create New Report</h2>
          {/* <h2 className="text-xl font-bold text-white">Add New Patient</h2> */}
          <button
            onClick={() => onClose()}
            className="text-black-200 hover:text-black-200 transition"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Demographics & Vitals */}
        <h3 className="font-semibold mt-2 mb-2">Patient Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Patient Name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
          />
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Age"
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
          <Select
            options={genderOptions}
            placeholder="Select Gender"
            value={genderOptions.find((Option) => Option.value === gender)}
            onChange={(e) => setGender(e?.value || "")}
          />
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Height"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
          />
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Weight"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Occupation"
            value={occupation}
            onChange={(e) => setOccupation(e.target.value)}
          />
        </div>

        {/* Clinical Assessment */}
        <h3 className="font-semibold mt-4 mb-2">Clinical Assessment</h3>
        <div className="space-y-3">
          <textarea
            className="w-full border p-2 rounded"
            rows={2}
            placeholder="Chief Complaints"
            value={chiefComplaints}
            onChange={(e) => setChiefComplaints(e.target.value)}
          />
          <div>
            <label className="font-semibold block mb-2">Physical Examination</label>
            <div className="space-y-3 p-3 bg-gray-50 rounded border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="Region"
                  value={associatedSymptom.region}
                  onChange={(e) =>
                    setAssociatedSymptom({
                      ...associatedSymptom,
                      region: e.target.value,
                    })
                  }
                />
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Inspection"
                  value={associatedSymptom.inspection}
                  onChange={(e) =>
                    setAssociatedSymptom({
                      ...associatedSymptom,
                      inspection: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Palpation"
                  value={associatedSymptom.palpation}
                  onChange={(e) =>
                    setAssociatedSymptom({
                      ...associatedSymptom,
                      palpation: e.target.value,
                    })
                  }
                />
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Range of Motion"
                  value={associatedSymptom.rangeOfMotion}
                  onChange={(e) =>
                    setAssociatedSymptom({
                      ...associatedSymptom,
                      rangeOfMotion: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Muscle Strength"
                  value={associatedSymptom.muscleStrength}
                  onChange={(e) =>
                    setAssociatedSymptom({
                      ...associatedSymptom,
                      muscleStrength: e.target.value,
                    })
                  }
                />
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Special Tests"
                  value={associatedSymptom.specialTests}
                  onChange={(e) =>
                    setAssociatedSymptom({
                      ...associatedSymptom,
                      specialTests: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
          <textarea
            className="w-full border p-2 rounded"
            rows={2}
            placeholder="Drug Allergy"
            value={drugAllergy}
            onChange={(e) => setDrugAllergy(e.target.value)}
          />
        </div>

        <h3 className="font-semibold mt-4 mb-2">Observations</h3>
        <textarea
          className="w-full border p-2 rounded"
          rows={3}
          placeholder="Comments..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        <h3 className="font-semibold mt-4 mb-2">Medicines</h3>

        {
          medicines.map((m, i) => (
            <div key={i} className="border p-3 rounded mb-2">
              <div className="flex items-center justify-between" >
                <input
                  type="text"
                  className="border p-2 w-full rounded"
                  placeholder="Medicine name"
                  value={m.name}
                  onChange={(e) => {
                    const copy = [...medicines];
                    copy[i].name = e.target.value;
                    setMedicines(copy);
                  }}
                />
                <button onClick={() => deleteMedicine(i)} className="ml-1 p-2 bg-red-600 text-white rounded-lg">
                  Delete
                </button>
              </div>
              <div className="flex items-center gap-2 align-center">
                <div className="flex items-center gap-2 mt-2">
                  <label className="font-medium">Days:</label>
                  <input
                    type="number"
                    min="1"
                    className="border p-1 rounded w-20"
                    value={m.days}
                    onChange={(e) => {
                      const copy = [...medicines];
                      copy[i].days = Number(e.target.value);
                      setMedicines(copy);
                    }}
                  />
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <label className="font-medium">Dosage:</label>
                  <input
                    type="text"
                    className="border p-1 rounded w-20"
                    value={m.dosage}
                    onChange={(e) => {
                      const copy = [...medicines];
                      copy[i].dosage = e.target.value;
                      setMedicines(copy);
                    }}
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <label className="font-medium">Route:</label>
                  <Select
                    options={routes}
                    getOptionLabel={(option: Route) => option.name}
                    getOptionValue={(option: Route) => option.id.toString()}
                    placeholder="Select route"
                    value={routes.find((r) => r.name === m.route) || null}
                    onChange={(e: Route | null) => {
                      if (e) {
                        const copy = [...medicines];
                        copy[i].route = e.name;
                        setMedicines(copy);
                      }
                    }}
                  />
                </div>

              </div>

              {["morning", "afternoon", "evening", "night"].map((t) => (
                <div key={t} className="flex gap-4 items-center mt-2 ml-2">
                  <label>
                    <input
                      type="checkbox"
                      checked={m[t].take}
                      onChange={() => {
                        const copy = [...medicines];
                        copy[i][t].take = !m[t].take;

                        if (!copy[i][t].take) {
                          copy[i][t].beforeFood = false;
                          copy[i][t].afterFood = false;
                        }

                        setMedicines(copy);
                      }}
                    />{" "}
                    <span className="w-20 capitalize">{t}</span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      disabled={!m[t].take}
                      checked={m[t].beforeFood}
                      onChange={() => {
                        const copy = [...medicines];
                        copy[i][t].beforeFood = !m[t].beforeFood;
                        setMedicines(copy);
                      }}
                    />{" "}
                    Before food
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      disabled={!m[t].take}
                      checked={m[t].afterFood}
                      onChange={() => {
                        const copy = [...medicines];
                        copy[i][t].afterFood = !m[t].afterFood;
                        setMedicines(copy);
                      }}
                    />{" "}
                    After food
                  </label>

                </div>
              ))}

            </div>
          ))
        }

        {/* <button onClick={addMedicine} className="mt-2 text-blue-600">
          ➕ Add Medicine
        </button> */}
        <button onClick={addMedicine} className="mt-1 p-2 bg-blue-600 text-white rounded-lg">
          Add Medicine
        </button>

        <h3 className="font-semibold mt-4 mb-2">Plan & Advice</h3>
        <div className="space-y-3">
          <textarea
            className="w-full border p-2 rounded"
            rows={2}
            placeholder="Advice / Instructions"
            value={advice}
            onChange={(e) => setAdvice(e.target.value)}
          />
          <textarea
            className="w-full border p-2 rounded"
            rows={2}
            placeholder="Exercise Notes"
            value={exerciseNotes}
            onChange={(e) => setExerciseNotes(e.target.value)}
          />
        </div>

        <h3 className="font-semibold mt-4 mb-2">Request for Diagnosis</h3>
        <textarea
          className="border w-full p-2 rounded"
          rows={3}
          value={requestForDiagnosis}
          onChange={(e) => setRequestForDiagnosis(e.target.value)}
        />

        <h3 className="font-semibold mt-4 mb-2">Follow-up date</h3>
        <input
          type="date"
          className="border p-2 rounded w-full"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
        />

        <div className="flex justify-end gap-3 mt-4">
          <button className="px-4 py-2 bg-gray-300" onClick={onClose}>
            Close
          </button>
          <button className="px-4 py-2 bg-green-600 text-white" onClick={save}>
            Save Report
          </button>
        </div>
      </div >
    </div >
  );
}
